import { useEffect, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import type { ArtistListItem, ArtistsListPageData } from '@/types/artist'

/*
  Figma: node 249:586 (Artists list)
  - Page heading: "Artists", h1-title (180px, alfarn-2, weight 700)
  - 2-column grid of artist cards (595px wide at 1440px), gap 40px
  - Card: 3:2 photo + name (28px alfarn-2)
  - Card photo placeholder bg: #C6C7C9, label "Artist Photo" in white 34.5px alfarn-2
  - Container padding: 24px horizontal, 80px vertical, gap 80px between rows
  - Clicking a card → /artists/{slug}

  Styling lives in src/styles/pages/artists.css.
*/

function ArtistCard({ artist }: { artist: ArtistListItem }) {
  const fullName = artist.name ?? ''
  const photoUrl =
    artist.main_photo?.versions?.card ||
    artist.main_photo?.versions?.large ||
    artist.main_photo?.url ||
    null

  return (
    <a href={`/artists/${artist.slug}`} className="mig-artist-card">
      <div className="mig-artist-card-photo">
        {photoUrl ? (
          <img src={photoUrl} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="mig-artist-card-placeholder">Artist Photo</span>
        )}
      </div>

      <p className="mig-artist-card-name">{fullName}</p>
    </a>
  )
}

export default function Artists() {
  const data = usePageData<ArtistsListPageData>()
  const artists = data?.artists ?? []

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Split artists into rows of 2 (2-column grid matching Figma)
  const rows: ArtistListItem[][] = []
  for (let i = 0; i < artists.length; i += 2) {
    rows.push(artists.slice(i, i + 2))
  }

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-artists-page-title">Artists</h1>
      </div>

      <div className="mig-artists-grid">
        {rows.length === 0 ? (
          <p className="mig-artists-empty">No artists yet. Check back soon.</p>
        ) : (
          rows.map((row, rowIndex) => (
            <div key={rowIndex} className="mig-artists-row">
              {row.map((artist) => (
                <ArtistCard key={artist.id} artist={artist} />
              ))}
              {row.length === 1 && (
                <div className="mig-artists-row-spacer" aria-hidden="true" />
              )}
            </div>
          ))
        )}
      </div>
    </section>
  )
}
